import {useCurrentCompanyId} from "@/api/companyApi";
import {
  MAX_KEYWORD_LENGTH,
  normalizeKeywordText,
  useTiktokVisibilityKeywordsInUseQuery,
} from "@/api/tiktokVisibilityApi";
import LockIcon from "@/assets/colorable/lock.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import Button from "@/components/Common/Button";
import CollapsibleContent from "@/components/Common/forms/CollapsibleContent";
import InputField from "@/components/Common/forms/InputField";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import clsxm from "@/lib/clsxm";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const SKELETON_PILL_WIDTHS = ["w-16", "w-20", "w-24", "w-14", "w-28", "w-18"] as const;

const ProductLineEditableSection: FC<{
  addButtonLabel?: string;
  currentItems: string[];
  inputPlaceholder?: string;
  isAddingDisabled?: boolean;
  isLoadingSuggestions?: boolean;
  lockable?: boolean;
  lockedHintText?: string;
  onAddCustom?: (value: string) => void;
  onToggle: (item: string) => void;
  pendingItems?: string[];
  removedCurrentItems?: string[];
  showInput?: boolean;
  suggestedItems?: string[];
  title: string;
  untrackedItems?: string[];
}> = ({
  addButtonLabel,
  currentItems,
  inputPlaceholder,
  isAddingDisabled,
  isLoadingSuggestions,
  lockable,
  lockedHintText,
  onAddCustom,
  onToggle,
  pendingItems = [],
  removedCurrentItems = [],
  showInput = true,
  suggestedItems = [],
  title,
  untrackedItems = [],
}) => {
  const {t} = useTranslation("search_visibility");
  const companyProfileId = useCurrentCompanyId();
  const {data: keywordsInUseData} = useTiktokVisibilityKeywordsInUseQuery({companyProfileId});
  const [inputValue, setInputValue] = useState("");
  const trimmed = normalizeKeywordText(inputValue);
  const totalCount = currentItems.length + pendingItems.length - removedCurrentItems.length - untrackedItems.length;
  const suggestedToShow = suggestedItems.filter(item => !pendingItems.includes(item));
  const lockedSet = new Set(
    lockable ? (keywordsInUseData?.locked_keywords ?? []).map(keyword => keyword.keyword_text.toLowerCase()) : [],
  );
  const hasLockedItems = currentItems.some(item => lockedSet.has(item.toLowerCase()));
  const untrackedSet = new Set(untrackedItems.map(item => item.toLowerCase()));

  const submit = () => {
    if (!trimmed || isAddingDisabled) return;
    const matchedSuggestion = suggestedItems.find(s => normalizeKeywordText(s).toLowerCase() === trimmed.toLowerCase());
    if (matchedSuggestion) {
      if (!pendingItems.includes(matchedSuggestion)) onToggle(matchedSuggestion);
    } else {
      onAddCustom?.(trimmed);
    }
    setInputValue("");
  };

  const renderPill = ({
    isDisabled,
    label,
    state,
  }: {
    isDisabled?: boolean;
    label: string;
    state: "current" | "locked" | "pending" | "removing" | "suggested";
  }) => (
    <button
      key={label}
      onClick={() => onToggle(label)}
      disabled={isDisabled || state === "locked"}
      title={label}
      className={clsxm(
        "group inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        state === "locked" && "border-neutral-white-dark bg-neutral-white-light text-neutral-grey cursor-not-allowed",
        state === "current" &&
          "border-primary-light-solid bg-primary-light-solid/40 text-neutral-black hover:bg-primary-light-solid/60",
        state === "removing" && !isDisabled && "border-danger-medium-red bg-danger-light-red text-danger-dark-red",
        state === "removing" &&
          isDisabled &&
          "border-danger-medium-red/50 bg-danger-light-red/60 text-danger-dark-red/70 cursor-not-allowed",
        state === "pending" &&
          "border-primary-medium-solid bg-primary-light-solid/30 text-primary-dark-solid hover:bg-primary-light-solid/50",
        state === "suggested" &&
          !isDisabled &&
          "text-neutral-grey-dark hover:border-primary-medium-solid hover:text-primary-dark-solid border-neutral-grey-light border-dashed bg-transparent",
        state === "suggested" &&
          isDisabled &&
          "text-neutral-grey-light border-neutral-grey-light cursor-not-allowed border-dashed bg-transparent opacity-60",
      )}>
      {state === "locked" && <LockIcon className="text-neutral-grey size-2.5 shrink-0" />}
      {state === "suggested" && <span>+</span>}
      <span className={clsxm("min-w-0 truncate", state === "removing" && "line-through")}>{label}</span>
      {state === "removing" && (
        <span className="bg-danger-medium-red rounded-full px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white uppercase">
          {t("removed")}
        </span>
      )}
      {(state === "current" || state === "pending" || state === "removing") && (
        <TimesIcon
          className={clsxm(
            "size-2 shrink-0 transition-opacity",
            state === "current" && "opacity-0 group-hover:opacity-100",
          )}
        />
      )}
    </button>
  );

  return (
    <div className="flex flex-col gap-3">
      <span className="text-neutral-grey-dark text-2xs font-semibold tracking-wider uppercase">
        {title} <span className="text-neutral-grey-light">({totalCount})</span>
      </span>
      <CollapsibleContent expanded lineHeight={34} shownLinesLimit={2.5} viewMoreClassName="text-xs">
        <div className="flex flex-wrap gap-2">
          {currentItems.map(item => {
            const isMarkedForRemoval = removedCurrentItems.includes(item);
            const isLocked = lockedSet.has(item.toLowerCase());
            const isUntracked = untrackedSet.has(item.toLowerCase());

            return renderPill({
              isDisabled: isMarkedForRemoval && isAddingDisabled,
              label: item,
              state: isLocked ? "locked" : isMarkedForRemoval ? "removing" : isUntracked ? "suggested" : "current",
            });
          })}
          {pendingItems.map(item => renderPill({label: item, state: "pending"}))}
          {suggestedToShow.map(item => renderPill({isDisabled: isAddingDisabled, label: item, state: "suggested"}))}
          {isLoadingSuggestions &&
            suggestedItems.length === 0 &&
            SKELETON_PILL_WIDTHS.map(width => <SkeletonLoader key={width} className={clsxm("h-6.5", width)} />)}
        </div>
      </CollapsibleContent>
      {lockedHintText && hasLockedItems && (
        <span className="text-neutral-grey text-2xs inline-flex items-center gap-1.5">
          <LockIcon className="text-neutral-grey size-2.5 shrink-0" />
          {lockedHintText}
        </span>
      )}
      {showInput && (
        <div className="flex items-stretch gap-2">
          <InputField
            containerClassName="flex-1"
            className="border-neutral-white-dark h-10 rounded-lg text-sm"
            isDisabled={isAddingDisabled}
            maxLength={MAX_KEYWORD_LENGTH}
            placeholder={inputPlaceholder}
            value={inputValue}
            onChange={setInputValue}
            onKeyDown={e => e.key === "Enter" && submit()}
          />
          <Button
            buttonStyle="Blue"
            className="h-10 shrink-0 rounded-lg border px-4"
            isDisabled={!trimmed || isAddingDisabled}
            onClick={submit}>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium">
              <PlusIcon className="size-3" />
              {addButtonLabel}
            </span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductLineEditableSection;
